import { useCallback, useState } from 'react';
import {
  HAVANA_OFFLINE_CACHE_NAME,
  readHavanaPackMeta,
  writeHavanaPackMeta,
  type HavanaPackMeta,
  type HavanaPackStatus,
} from '../core/maps/havanaOfflinePack';
import { mapTilesFromImportMeta } from '../core/maps/mapTilesConfig';

/**
 * Download / cache Greater Havana offline pack (PMTiles URL via env).
 */
export function useHavanaOfflinePack() {
  const tiles = mapTilesFromImportMeta();
  const [meta, setMeta] = useState<HavanaPackMeta | null>(() => readHavanaPackMeta());
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);

  const status: HavanaPackStatus = (() => {
    if (!tiles.pmtilesUrl) return 'unavailable';
    if (busy) return 'downloading';
    if (meta?.status === 'ready' && meta.url === tiles.pmtilesUrl) return 'ready';
    if (meta?.status === 'error') return 'error';
    return meta?.status ?? 'unavailable';
  })();

  const download = useCallback(async () => {
    const url = tiles.pmtilesUrl;
    if (!url || busy) return;
    setBusy(true);
    setProgress(0);
    writeHavanaPackMeta({ url, savedAt: Date.now(), status: 'downloading' });
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const total = Number(res.headers.get('content-length') || 0);
      const reader = res.body?.getReader();
      const chunks: BlobPart[] = [];
      let received = 0;
      if (reader) {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          if (value) {
            chunks.push(value);
            received += value.length;
            if (total > 0) setProgress(Math.min(99, Math.round((received / total) * 100)));
          }
        }
      } else {
        const buf = await res.arrayBuffer();
        chunks.push(buf);
        received = buf.byteLength;
      }
      const blob = new Blob(chunks);
      if (typeof caches !== 'undefined') {
        const cache = await caches.open(HAVANA_OFFLINE_CACHE_NAME);
        await cache.put(url, new Response(blob, {
          headers: { 'Content-Type': 'application/vnd.pmtiles' },
        }));
      }
      const next: HavanaPackMeta = {
        url,
        savedAt: Date.now(),
        bytes: received,
        status: 'ready',
      };
      writeHavanaPackMeta(next);
      setMeta(next);
      setProgress(100);
    } catch {
      const next: HavanaPackMeta = {
        url,
        savedAt: Date.now(),
        status: 'error',
      };
      writeHavanaPackMeta(next);
      setMeta(next);
    } finally {
      setBusy(false);
    }
  }, [tiles.pmtilesUrl, busy]);

  return {
    status,
    progress,
    busy,
    packUrl: tiles.pmtilesUrl,
    download,
    meta,
  };
}
